import styled from "styled-components"
import { CONFIDENCE_LOW, CONFIDENCE_MEDIUM, CONFIDENCE_HIGH } from "const"
import { Container } from "client/components"
import { ConfidenceType } from "client/utils/types"


export const KeyResultGraphWrapper = styled(Container)`
    position: relative;
    padding: 15px 15px 20px;
    margin-bottom: 15px;
    background: ${(props) => props.theme.pageContentBackground};
`
export const GraphTitle = styled.div<{
    topOffset: number
}>`
    position: absolute;
    right: 35px;
    top: ${(props) => props.topOffset}px;
    display: flex;
    align-items: center;
    padding: 4px 8px;
    border: 1px solid ${(props) => props.theme.keyResultGraphTitleBorderColor};
    text-transform: uppercase;
    font-family: ${(props) => props.theme.openSans};
    font-size: 12px;
    font-weight: bold;

    & > i {
        margin-right: 10px;
    }
`


// background sections for stay above/below
const GraphSection = styled.path`
    stroke-width: 0;
`
export const GraphSuccessSection = styled(GraphSection)`
    fill: ${(props) => props.theme.keyResultGraphSuccessSection};
`
export const GraphCriticalSection = styled(GraphSection)`
    fill: ${(props) => props.theme.keyResultGraphCriticalSection};
`
export const GraphFailSection = styled(GraphSection)`
    fill: ${(props) => props.theme.keyResultGraphFailSection};
`


export const GridLine = styled.path`
    stroke-width: 1;
    stroke: ${(props) => props.theme.keyResultGraphGridColor};
`
const GridText = styled.text`
    font-size: 13px;
    font-family: system-ui;
    fill: ${(props) => props.theme.defaultTextColor};
`
export const GridHorizontalLineText = styled(GridText)`
    text-anchor: end;
    transform: translate(-6px, 5px);
`
export const GridVerticalLineText = styled(GridText)`
    text-anchor: middle;
    transform: translateY(16px);
`


export const GraphUpdateLine = styled.path`
    stroke: ${(props) => props.theme.keyResultGraphUpdateColor};
    stroke-width: 3;
    fill: none;
`
export const GraphUpdateLineFilled = styled.path`
    stroke-width: 0;
    fill: ${(props) => props.theme.keyResultGraphUpdateFillColor};
`
export const GraphUpdatePoint = styled.circle`
    fill: ${(props) => props.theme.keyResultGraphUpdateColor};
    opacity: 0;
    transition: "0.1s ease";
`
export const GraphAchievedLine = styled.path<{
    achieved: boolean
}>`
    stroke: none;
    fill: ${(props) => (props.achieved ? props.theme.keyResultGraphAchievedColor : props.theme.keyResultGraphNotAchievedColor)};
`


export const GraphCrossLine = styled.line`
    stroke-width: 1px;
    stroke: ${(props) => props.theme.keyResultGraphCrossLineColor};
    stroke-dasharray: 5 3;
    opacity: 0;
    pointer-events: none;
`
export const GraphTargetLine = styled.line`
    stroke-width: 1px;
    stroke: ${(props) => props.theme.keyResultGraphTargetColor};
    stroke-dasharray: 5 3;
    pointer-events: none;
`
export const GraphTargetPoint = styled.circle`
    fill: ${(props) => props.theme.keyResultGraphTargetColor};
    transition: "0.1s ease";
`


export const GraphConfidenceLine = styled.path`
    stroke-width: 3;
    fill: none;
`
export const GraphConfidencePoint = styled.circle<{
    confidenceLevel: ConfidenceType
}>`
    fill: ${(props) => {
        if (props.confidenceLevel === CONFIDENCE_LOW) return props.theme.confidenceLowBackground
        if (props.confidenceLevel === CONFIDENCE_MEDIUM) return props.theme.confidenceMediumBackground
        if (props.confidenceLevel === CONFIDENCE_HIGH) return props.theme.confidenceHighBackground
        return "none"
    }};
`


export const GraphConfidenceGradientLow = styled.stop`
    stop-color: ${(props) => props.theme.confidenceLowBackground};
`
export const GraphConfidenceGradientMedium = styled.stop`
    stop-color: ${(props) => props.theme.confidenceMediumBackground};
`
export const GraphConfidenceGradientHigh = styled.stop`
    stop-color: ${(props) => props.theme.confidenceHighBackground};
`


const Deadline = styled.path`
    stroke-width: 2px;
    opacity: 0.6;
    transition: ${(props) => props.theme.defaultTransition};
`
export const SoftDeadline = styled(Deadline)`
    stroke: ${(props) => props.theme.keyResultGraphSoftDeadlineColor};
`
export const HardDeadline = styled(Deadline)`
    stroke: ${(props) => props.theme.keyResultGraphHardDeadlineColor};
`


const DeadlineTooltip = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 4px 8px;
    white-space: nowrap;
    font-size: 11px;
    font-family: system-ui;
    line-height: 14px;
    border-radius: 2px;
    opacity: 0;
    color: ${(props) => props.theme.defaultTextColor};
    transition: opacity 0.1s ease;
    pointer-events: none;
    z-index: 1000;
`
export const SoftDeadlineTooltip = styled(DeadlineTooltip)`
    border: 1px solid ${(props) => props.theme.keyResultGraphSoftDeadlineColor};
    background: ${(props) => props.theme.keyResultGraphSoftDeadlineBackground};
`
export const HardDeadlineTooltip = styled(DeadlineTooltip)`
    border: 1px solid ${(props) => props.theme.keyResultGraphHardDeadlineColor};
    background: ${(props) => props.theme.keyResultGraphHardDeadlineBackground};
`


export const UpdateTooltip = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    width: fit-content;
    min-width: 110px;
    padding: 8px 12px;
    border-radius: 2px;
    opacity: 0;
    color: ${(props) => props.theme.defaultTextColor};
    background: ${(props) => props.theme.keyResultGraphTooltipBackground};
    pointer-events: none;
    z-index: 1001;
`
export const UpdateTooltipTitle = styled.div`
    margin: 4px 0;
    font-size: 12px;
`
export const UpdateTooltipValueWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: baseline;
    white-space: nowrap;
`
export const UpdateTooltipPoint = styled.div<{
    typePoint: "update" | "target" | "achieved" | "notAchieved"
}>`
    height: 10px;
    width: 10px;
    border-radius: 50%;
    margin: 4px 4px 4px 0;
    background: ${(props) => {
        if (props.typePoint === "update") return props.theme.keyResultGraphTooltipPointUpdate
        if (props.typePoint === "target") return props.theme.keyResultGraphTooltipPointTarget
        if (props.typePoint === "achieved") return props.theme.keyResultGraphTooltipPointAchieved
        if (props.typePoint === "notAchieved") return props.theme.keyResultGraphTooltipPointNotAchieved
        return "none"
    }};
`
export const UpdateTooltipValue = styled.div`
    font-size: 14px;
`
export const UpdateTooltipValueDiff = styled.div<{
    value: number
}>`
    margin-left: 15px;
    font-size: 14px;
    color: ${(props) => {
        if (props.value > 0) return props.theme.keyResultGraphTooltipDiffAboveZeroColor
        if (props.value < 0) return props.theme.keyResultGraphTooltipDiffBelowZeroColor
        return props.theme.keyResultGraphTooltipDiffZeroColor
    }}
`
